// CLIENT RECORD (UI rebuild batch 5) — one person's record over the Onboarding list (?lead=<session id>), in the kit's
// DetailSheet: beside the list from lg, the whole screen on the phone. Escape closes it.
//
// Astra (onboarding-record): open on the identity and what is unresolved; separate Evidence, Conversation and History;
// the decision stays in its channel. So, top to bottom: the headline (Buyer / Seller · amount coin · method), the stage
// and when the record was read; the ways to them (their chat, the card in the ops group, their phone); the seven checklist
// sections — the line in view, the detail and "Ask the customer" behind a tap (the four older checks when the worker sends
// no checklist); what is still missing; then three folds — Evidence (the wallet with its transactions and Arkham, the ID
// images), Conversation, History (?tab=evidence|chat|history); and Make a party, the one gold primary, once approved.
//
// KEPT from today's record (screens/OnboardingRecord.tsx): the same read and query key, read again every minute; a failed
// or stale read says so and keeps what it had, with Try again; a lead no longer on record says so; ?tab= and ?ask=;
// Read again for the wallet's chain; Ask the customer; Make a party with the name-taken choice; the chat button; the
// card link; the phone with WhatsApp; the ID image links; every chat; the history and the decision. Nothing decides here.
import { useEffect, useRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../../lib/errors';
import { DetailSheet, StateLine } from '../kit';
import type { StateTone } from '../../kit';
import { CardButton, ChatButton, READ_EVERY_MS, failMore, failSaid, readOf, targetOf, type Read } from '../../OnboardingParts';
import { onboardingApi, type CheckKey, type LeadRecord } from '../../../lib/onboarding';
import { drawnOf, headline, SectionRows, tradeParts, type ClaimLike, type Drawn } from './Checklist';
import { Answers, AskForm, Chat, History, IdImages, PartyAction, PhoneLine, RequestTrail, StillMissing, WalletEvidence, trailOf } from './RecordParts';
import { ChevronDown } from 'lucide-react';

export type Fold = 'evidence' | 'chat' | 'history';
/** What the list knows that the record does not: the stage in its queue, the warnings its row carries, and its claim
 *  (the method rides on the row's claim). */
export interface RowSummary {
  standing: { text: string; tone: 'bad' | 'warn' | null } | null;
  strip: { text: string; tone: 'bad' | 'warn' } | null;
  claim: ClaimLike | null;
}

const STATE_WORD: Record<string, string> = { new: 'In the chat with the bot', awaiting_approval: 'Waiting for a decision', kyc_pending: 'KYC pending', done: 'Approved', rejected: 'Rejected' };
const TONE_INK = { bad: 'text-neg', warn: 'text-warn' } as const;

export function ClientRecord({ id, row, fold, setFold, asking, setAsking, close }: {
  id: string; row: RowSummary | null; fold: Fold | null; setFold: (f: Fold | null) => void;
  asking: CheckKey | null; setAsking: (k: CheckKey | null) => void; close: () => void;
}) {
  const q = useQuery({ queryKey: ['onboarding', 'lead', id], queryFn: () => onboardingApi.lead(id), refetchInterval: READ_EVERY_MS });
  const read: Read<LeadRecord> = readOf(q, () => true);
  const top = useRef<HTMLDivElement>(null);
  // The record takes the focus when it opens, so a keyboard and a reader start in it.
  useEffect(() => { top.current?.focus(); }, [id]);
  const d = read.d;
  const name = d ? d.name ?? d.who ?? `Lead ${id.slice(0, 8)}` : `Lead ${id.slice(0, 8)}`;
  const gone = q.error instanceof ApiError && q.error.code === 'no_such_lead';
  const reading = q.fetchStatus === 'fetching';
  return (
    <DetailSheet title={<span className="line-clamp-2 break-words" title={name}>{name}</span>} onClose={close} wide
      footer={d && (d.party || d.state === 'done') ? <PartyAction d={d} /> : undefined}>
      <div ref={top} tabIndex={-1} className="space-y-5 !outline-none" aria-label={`The record of ${name}`}>
        <ReadLines q={q} read={read} gone={gone} retry={() => void q.refetch()} />
        {d && <Body d={d} id={id} row={row} read={read} reading={reading} readAgain={() => void q.refetch()}
          fold={fold} setFold={setFold} asking={asking} setAsking={setAsking} />}
      </div>
    </DetailSheet>
  );
}

function Body({ d, id, row, read, reading, readAgain, fold, setFold, asking, setAsking }: {
  d: LeadRecord; id: string; row: RowSummary | null; read: Read<LeadRecord>; reading: boolean; readAgain: () => void;
  fold: Fold | null; setFold: (f: Fold | null) => void; asking: CheckKey | null; setAsking: (k: CheckKey | null) => void;
}) {
  const a = d.answers;
  // The record's own answers, with the row's method when the record carries none (a worker that writes it on claims only).
  const claim: ClaimLike = {
    side: a.side, asset: a.asset, sizeBand: a.sizeBand, type: a.type ?? null, amountAed: a.amountAed ?? null,
    method: a.method !== undefined ? a.method : row?.claim?.method ?? null,
  };
  const head = headline(claim, a.size) ?? 'No trade on record';
  const trade = tradeParts(claim, a.size);
  const standing = row?.standing ?? (d.state ? { text: STATE_WORD[d.state] ?? d.state, tone: null } : null);
  const drawn = drawnOf(d.checklist, d.assessment);
  const canAsk = Array.isArray(d.requests);
  const name = d.name ?? d.who ?? `Lead ${id.slice(0, 8)}`;
  const extra = (s: Drawn): ReactNode => {
    if (!s.ask) return null;
    const trail = trailOf(d, [s.ask]);
    if (!canAsk && trail.length === 0) return null;
    return (
      <div className="space-y-2">
        <RequestTrail list={trail} />
        {canAsk && (
          <button type="button" className="btn-link text-body max-lg:py-2.5" aria-expanded={asking === s.ask} data-ask={s.ask}
            onClick={() => setAsking(asking === s.ask ? null : s.ask)}>
            Ask the customer
          </button>
        )}
      </div>
    );
  };
  const others = trailOf(d, [null]);
  return (
    <>
      {/* Who and what: the headline, the stage, when it was read — and the row's warnings no section says. */}
      <section className="space-y-1" aria-label="Who and what">
        {/* ONE FACT A LINE (his "one fact per place", 1 Nov 2026): the side, the size and coin as the figure, how it is paid;
            then the stage, then when it was read — they were two packed lines. */}
        {/* The whole name, wrapping — the sheet's title is cut at its edge (Astra, 1 Nov 2026). */}
        <p className="break-words text-body font-medium" data-name="">{name}</p>
        <div data-headline="" title={head}>
          {(trade.side || trade.asset) && <p className="text-title font-semibold">{trade.side ?? 'Trade'}{trade.asset && <span className="font-normal text-tg-hint"> · {trade.asset}</span>}</p>}
          {trade.amount && <p className="font-mono tabular text-body">{trade.amount}</p>}
          {trade.method && <p className="text-meta text-tg-hint">{trade.method}</p>}
          {!trade.side && !trade.amount && !trade.asset && !trade.method && <p className="text-title font-semibold">{head}</p>}
        </div>
        {d.who && d.who !== name && <p className="truncate text-meta text-tg-hint" title={d.who}>{d.who}</p>}
        {standing && <p className={`text-body ${standing.tone ? TONE_INK[standing.tone] : 'text-tg-text'}`} data-standing="">{standing.text}</p>}
        <p className="text-meta text-tg-hint">Read {read.readWords}{reading && ' · reading again…'}</p>
        {row?.strip && <p className={`text-meta ${TONE_INK[row.strip.tone]}`} data-strip={row.strip.tone}>{row.strip.text}</p>}
      </section>

      {/* The ways to them: their chat, the card in the ops group, their phone. */}
      <section className="flex flex-wrap items-center gap-x-4 gap-y-2" aria-label="Reach them">
        <ChatButton t={targetOf(d, { sessionId: d.id }, name)} />
        {d.cardUrl ? <CardButton url={d.cardUrl} says="Card in the ops group" />
          : d.ref && <span className="text-meta text-tg-hint">No card link · ref <span className="font-mono">{d.ref}</span></span>}
        {d.phone && <PhoneLine p={d.phone} />}
      </section>

      {/* The checks: the seven (or the four older ones), each line in view, the detail behind a tap. */}
      <section className="space-y-3" aria-label="Checks">
        {drawn
          ? <SectionRows drawn={drawn.rows} label={drawn.kind === 'checklist' ? 'Onboarding checklist' : 'The four checks'} extra={extra} />
          : <p className="text-body text-tg-hint">Checks come with the bot&apos;s next update.</p>}
        {canAsk && asking && <AskForm key={asking} d={d} check={asking} close={() => setAsking(null)} />}
        <StillMissing d={d} readMs={read.readMs} />
      </section>

      {/* Evidence, the conversation and the history — each behind its own tap. */}
      <div className="divide-y divide-stroke-soft rounded-[10px] border border-stroke">
        <FoldRow id="evidence" label="Evidence" open={fold} setOpen={setFold}>
          <div className="space-y-5">
            <Group title="Wallet"><WalletEvidence d={d} readMs={read.readMs} reading={reading} readAgain={readAgain} /></Group>
            <Group title="ID images"><IdImages d={d} /></Group>
            {/* An older worker sends no checklist: its answers, caveats and what changed are said here instead. */}
            {drawn?.kind !== 'checklist' && <Group title="Answers"><Answers d={d} /></Group>}
            {others.length > 0 && <Group title="Other questions"><RequestTrail list={others} /></Group>}
          </div>
        </FoldRow>
        <FoldRow id="chat" label="Conversation" count={d.chat.length} open={fold} setOpen={setFold}><Chat d={d} readMs={read.readMs} /></FoldRow>
        <FoldRow id="history" label="History" count={d.timeline.length} open={fold} setOpen={setFold}><History d={d} /></FoldRow>
      </div>
    </>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2" aria-label={title}>
      <h4 className="text-label font-medium uppercase tracking-wider text-tg-hint">{title}</h4>
      {children}
    </section>
  );
}

/** One fold: its name (and how many), the chevron; its body under it while open. One open at a time (?tab=). */
function FoldRow({ id, label, count, open, setOpen, children }: {
  id: Fold; label: string; count?: number; open: Fold | null; setOpen: (f: Fold | null) => void; children: ReactNode;
}) {
  const on = open === id;
  return (
    <div data-fold={id}>
      <button type="button" aria-expanded={on} onClick={() => setOpen(on ? null : id)}
        className="flex min-h-[44px] w-full items-center justify-between gap-3 px-4 text-left text-body hover:bg-panel-2 lg:min-h-[40px]">
        <span className="font-medium">{label}{count !== undefined && <span className="ml-2 font-mono tabular text-meta text-tg-hint">{count}</span>}</span>
        <ChevronDown className={`h-4 w-4 text-tg-hint transition-transform ${on ? 'rotate-180' : ''}`} aria-hidden />
      </button>
      {on && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );
}

/** What is wrong with the record's read — ONE line each, with Try again where reading again can help: not read yet,
 *  failed (keeping what it had, said to be the earlier read), no connection, more than ten minutes old, or gone. */
function ReadLines({ q, read, gone, retry }: { q: { isError: boolean; error: unknown; fetchStatus: string }; read: Read<LeadRecord>; gone: boolean; retry: () => void }) {
  const box = (line: ReactNode, tone: StateTone = 'warn', more?: ReactNode, action?: ReactNode) => (
    <div data-record-note=""><StateLine tone={tone} more={more} action={action}>{line}</StateLine></div>
  );
  const tryAgain = <button type="button" className="btn-link text-meta max-lg:py-2.5" onClick={retry}>Try again</button>;
  if (!read.d) {
    if (q.isError) {
      if (gone) return box('This lead is no longer on record.');
      const f = failSaid("Couldn't read the record", q.error);
      return box(f.line, 'warn', f.more, tryAgain);
    }
    if (q.fetchStatus === 'paused') return box('No connection — the record reads when back.');
    return box('Reading the record…', 'neutral');
  }
  if (q.isError) {
    return gone
      ? box('No longer on record — showing the last read.')
      : box(<>Record refresh failed — read {read.readWords}.</>, 'warn', failMore(q.error), tryAgain);
  }
  if (q.fetchStatus === 'paused') return box(<>No connection — record read {read.readWords}.</>, 'warn', 'It is read again as soon as the connection is back.');
  if (!read.fresh) return box(<>Stale record — read {read.readWords}.</>);
  return null;
}
